import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCourses, getCourseLeaderboard, reportIssue } from '../api';
import { Spinner, EmptyState, ScoreBadge, Avatar, ErrorMsg } from '../components/UI';

function CourseCard({ course }) {
  const navigate = useNavigate();
  const [leader, setLeader] = useState(undefined);
  const [loadingLeader, setLoadingLeader] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleExpand = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (leader !== undefined) return; // already loaded
    setLoadingLeader(true);
    try {
      const lb = await getCourseLeaderboard(course.id);
      setLeader(lb[0] || null);
    } catch {
      setLeader(null);
    } finally {
      setLoadingLeader(false);
    }
  };

  return (
    <div className="card overflow-hidden">
      <button
        className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-amber-50/50 transition-colors"
        onClick={handleExpand}
      >
        <div>
          <h3 className="font-serif font-semibold text-green-900 text-base">{course.name}</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {[course.city, course.state, course.country].filter(Boolean).join(', ')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loadingLeader && <Spinner size="sm" />}
          <span className={`text-gray-400 text-sm transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
            ▾
          </span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-amber-50 px-4 pb-4 pt-3 fade-in">
          {loadingLeader ? (
            <div className="flex justify-center py-2"><Spinner size="sm" /></div>
          ) : leader ? (
            <div className="flex items-center gap-3 mb-3 px-1 flex-wrap">
              <span className="text-xs font-semibold text-amber-600 uppercase tracking-widest">Leader</span>
              <Avatar username={leader.username} url={leader.avatar_url} size="sm" />
              <span className="text-sm font-medium text-green-900">{leader.username}</span>
              <ScoreBadge overUnder={leader.over_under_par} />
              {leader.tee_color && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <span
                    className="w-2.5 h-2.5 rounded-full border border-gray-300 flex-shrink-0"
                    style={{ backgroundColor: leader.tee_color.toLowerCase() === 'white' ? '#f0f0f0' : leader.tee_color.toLowerCase() }}
                  />
                  <span className="capitalize">{leader.tee_color}</span>
                  {leader.tee_yardage && <span>· {leader.tee_yardage.toLocaleString()} yds</span>}
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 mb-3 px-1">No scores yet — be the first!</p>
          )}

          <button
            className="w-full flex items-center justify-between py-2.5 px-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors group"
            onClick={() => navigate(`/leaderboard/course/${course.id}`)}
          >
            <span className="text-sm font-medium text-green-800">View Course Leaderboard</span>
            <span className="text-green-600 group-hover:translate-x-0.5 transition-transform text-sm">→</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ── Request Course Modal ──────────────────────────────────────────────────────
function RequestCourseModal({ initialName = '', onClose }) {
  const [name,    setName]    = useState(initialName);
  const [city,    setCity]    = useState('');
  const [state,   setState]   = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim())    { setError('Course name is required.'); return; }
    if (!country.trim()) { setError('Country is required.'); return; }

    const description = [
      '[COURSE REQUEST]',
      `Course: ${name.trim()}`,
      city.trim()    ? `City: ${city.trim()}`       : null,
      state.trim()   ? `State: ${state.trim()}`     : null,
      `Country: ${country.trim()}`,
    ].filter(Boolean).join(' | ');

    if (description.length > 300) {
      setError('Request is too long. Please shorten the course name or location details.');
      return;
    }

    setLoading(true);
    try {
      await reportIssue(description);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div className="card w-full max-w-sm p-6 fade-in">
        {success ? (
          <>
            <div className="text-center py-4">
              <div className="text-4xl mb-3">📬</div>
              <h3 className="font-serif font-bold text-xl text-green-900 mb-2">Request Sent!</h3>
              <p className="text-sm text-gray-500">
                Thanks! Your course request has been submitted. We'll review it and add it as soon as possible.
              </p>
            </div>
            <button onClick={onClose} className="btn-primary w-full mt-5">Done</button>
          </>
        ) : (
          <>
            <h3 className="font-serif font-bold text-xl text-green-900 mb-1">Request a Course</h3>
            <p className="text-sm text-gray-500 mb-5">
              Can't find your course? Fill in what you know and we'll add it.
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                  Course Name <span className="text-red-400">*</span>
                </label>
                <input
                  className="input-field"
                  placeholder="e.g. Pebble Beach Golf Links"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  maxLength={100}
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">City</label>
                <input
                  className="input-field"
                  placeholder="e.g. Pebble Beach"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  maxLength={60}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">State / Province</label>
                <input
                  className="input-field"
                  placeholder="e.g. California"
                  value={state}
                  onChange={e => setState(e.target.value)}
                  maxLength={60}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                  Country <span className="text-red-400">*</span>
                </label>
                <input
                  className="input-field"
                  placeholder="e.g. United States"
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  maxLength={60}
                  required
                />
              </div>
              <ErrorMsg message={error} />
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={onClose} className="btn-secondary flex-1" disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Submit Request'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [courses,          setCourses]          = useState([]);
  const [filtered,         setFiltered]         = useState([]);
  const [search,           setSearch]           = useState('');
  const [loading,          setLoading]          = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);

  useEffect(() => {
    getCourses()
      .then(data => {
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setCourses(sorted);
        setFiltered(sorted);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      courses.filter(c =>
        !q ||
        c.name?.toLowerCase().includes(q) ||
        c.city?.toLowerCase().includes(q) ||
        c.state?.toLowerCase().includes(q) ||
        c.country?.toLowerCase().includes(q)
      )
    );
  }, [search, courses]);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-serif font-bold text-3xl text-green-900">Leaderboards</h1>
          <p className="text-gray-500 text-sm mt-1">Click a course to view its leaderboard</p>
        </div>
        <button onClick={() => navigate('/add-course')} className="btn-gold flex-shrink-0">
          + Add Course
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-2">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></span>
        <input
          type="text"
          className="input-field pl-9"
          placeholder="🔍 Search by course name, city, state, or country..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
          >
            ✕
          </button>
        )}
      </div>

      {/* Request course hint — visible whenever user is searching */}
      {search && (
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => setShowRequestModal(true)}
            className="text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors"
          >
            📬 Can't find it? Request this course →
          </button>
        </div>
      )}

      {/* Courses list */}
      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="🏌️"
          title={search ? 'No courses match your search' : 'No courses yet'}
          message={search ? 'Try a different keyword, add the course yourself, or request we add it.' : 'Be the first to add a course!'}
          action={
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => navigate('/add-course')} className="btn-gold">
                + Add Course
              </button>
              {search && (
                <button
                  onClick={() => setShowRequestModal(true)}
                  className="btn-secondary"
                >
                  📬 Request This Course
                </button>
              )}
            </div>
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map(course => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      )}

      {courses.length > 0 && (
        <p className="text-center text-xs text-gray-400 mt-6">
          {filtered.length} of {courses.length} course{courses.length !== 1 ? 's' : ''}
        </p>
      )}

      {showRequestModal && (
        <RequestCourseModal
          initialName={search}
          onClose={() => setShowRequestModal(false)}
        />
      )}
    </div>
  );
}